/**
 * Serviço de WhatsApp
 *
 * Funções para comunicação com a API do WhatsApp.
 *
 * @author Equipe VendeAi
 * @since 1.0.0
 */

import { api } from './api';
import { ApiResponse, ApiMeta, PaginationParams } from '@/types/common';
import {
  WhatsAppStatus,
  WhatsAppMessage,
  WhatsAppContact,
  SendMessageDTO,
  WhatsAppCatalogOverview,
  WhatsAppProductCatalog,
} from '@/types/whatsapp';

/**
 * Obtém status da conexão WhatsApp
 *
 * @returns Status da conexão
 */
export async function getStatus(): Promise<WhatsAppStatus> {
  const response = await api.get<ApiResponse<WhatsAppStatus>>(
    '/whatsapp/status',
  );

  if (!response.data.success) {
    throw new Error(response.data.error?.message || 'Erro ao buscar status');
  }

  return response.data.data;
}

/**
 * Conecta ao WhatsApp
 *
 * @returns Dados da conexão
 */
export async function connect(): Promise<{
  instanceId: string;
  status: string;
  qrCode?: string;
}> {
  const response = await api.post<
    ApiResponse<{ instanceId: string; status: string; qrCode?: string }>
  >('/whatsapp/connect');

  if (!response.data.success) {
    throw new Error(response.data.error?.message || 'Erro ao conectar');
  }

  return response.data.data;
}

/**
 * Desconecta do WhatsApp
 */
export async function disconnect(): Promise<void> {
  const response = await api.post<ApiResponse<void>>('/whatsapp/disconnect');

  if (!response.data.success) {
    throw new Error(response.data.error?.message || 'Erro ao desconectar');
  }
}

/**
 * Faz logout do WhatsApp
 */
export async function logout(): Promise<void> {
  const response = await api.post<ApiResponse<void>>('/whatsapp/logout');

  if (!response.data.success) {
    throw new Error(response.data.error?.message || 'Erro ao fazer logout');
  }
}

/**
 * Obtém QR Code para conexão
 *
 * @returns QR Code
 */
export async function getQrCode(): Promise<{ qrCode: string }> {
  const response = await api.get<ApiResponse<{ qrCode: string }>>(
    '/whatsapp/qrcode',
  );

  if (!response.data.success) {
    throw new Error(response.data.error?.message || 'Erro ao buscar QR Code');
  }

  return response.data.data;
}

/**
 * Envia uma mensagem via WhatsApp
 *
 * @param data - Dados da mensagem
 * @returns Mensagem enviada
 */
export async function sendMessage(
  data: SendMessageDTO,
): Promise<WhatsAppMessage> {
  const response = await api.post<ApiResponse<WhatsAppMessage>>(
    '/whatsapp/messages/send',
    data,
  );

  if (!response.data.success) {
    throw new Error(response.data.error?.message || 'Erro ao enviar mensagem');
  }

  return response.data.data;
}

/**
 * Lista mensagens com filtros e paginação
 *
 * @param params - Parâmetros de busca
 * @returns Lista de mensagens e metadados
 */
export async function listMessages(
  params: PaginationParams = {},
): Promise<{ data: WhatsAppMessage[]; meta: ApiMeta }> {
  const queryParams = new URLSearchParams();

  // Adiciona parâmetros
  Object.entries(params).forEach(([key, value]) => {
    if (value !== undefined && value !== null && value !== '' && value !== 'all') {
      queryParams.append(key, String(value));
    }
  });

  const response = await api.get<ApiResponse<WhatsAppMessage[]>>(
    `/whatsapp/messages?${queryParams.toString()}`,
  );

  if (!response.data.success) {
    throw new Error(response.data.error?.message || 'Erro ao buscar mensagens');
  }

  return {
    data: response.data.data,
    meta: response.data.meta!,
  };
}

/**
 * Lista contatos do WhatsApp
 *
 * @returns Lista de contatos
 */
export async function listContacts(): Promise<WhatsAppContact[]> {
  const response = await api.get<ApiResponse<WhatsAppContact[]>>(
    '/whatsapp/contacts',
  );

  if (!response.data.success) {
    throw new Error(response.data.error?.message || 'Erro ao buscar contatos');
  }

  return response.data.data;
}

/**
 * Interfaces para configurações do chatbot
 */
export interface MessageTemplateInfo {
  key: string;
  label: string;
  description: string;
  variables: string[];
  group: string;
}

export interface ChatbotConfig {
  sessionTimeoutMinutes: number;
  maxInvalidAttempts: number;
  cartRecoveryEnabled: boolean;
  cartRecoveryDelayMinutes: number;
  cartRecoveryCheckIntervalMinutes: number;
  cartExpirationDays: number;
  cartRecoveryMaxAttempts: number;
  cartRecoveryOfferType: 'NONE' | 'DISCOUNT_PERCENT' | 'DISCOUNT_FIXED' | 'FREE_SHIPPING';
  cartRecoveryOfferValue: number;
  cartRecoveryCouponCode: string;
}

export interface ChatbotSettings {
  autoReplyEnabled: boolean;
  chatbotEnabled: boolean;
  welcomeMessage: string;
  awayMessage: string;
  businessHours: any;
  messages: Record<string, string>;
  defaults: Record<string, string>;
  templateInfo: MessageTemplateInfo[];
  chatbotConfig: ChatbotConfig;
  chatbotConfigDefaults: ChatbotConfig;
}

/**
 * Obtém configurações do chatbot
 */
export async function getChatbotSettings(): Promise<ChatbotSettings> {
  const response = await api.get<ApiResponse<ChatbotSettings>>(
    '/whatsapp/chatbot/settings',
  );

  if (!response.data.success) {
    throw new Error(response.data.error?.message || 'Erro ao buscar configurações');
  }

  return response.data.data;
}

/**
 * Atualiza configurações do chatbot
 */
export async function updateChatbotSettings(data: Partial<{
  autoReplyEnabled: boolean;
  chatbotEnabled: boolean;
  welcomeMessage: string;
  awayMessage: string;
  businessHours: any;
  messages: Record<string, string>;
  chatbotConfig: Partial<ChatbotConfig>;
}>): Promise<void> {
  const response = await api.put<ApiResponse<void>>(
    '/whatsapp/chatbot/settings',
    data,
  );

  if (!response.data.success) {
    throw new Error(response.data.error?.message || 'Erro ao atualizar configurações');
  }
}

/**
 * Interfaces para leads e carrinhos abandonados
 */
export interface WhatsAppLead {
  id: string;
  tenantId: string;
  remoteJid: string;
  phoneNumber: string;
  pushName: string | null;
  status: 'NEW' | 'ENGAGED' | 'CART_ABANDONED' | 'CONVERTED' | 'LOST';
  interactionCount: number;
  cartAbandonCount: number;
  firstInteractionAt: string;
  lastInteractionAt: string;
  customerId: string | null;
  convertedAt: string | null;
  createdAt: string;
  /** Foto do WhatsApp (do cliente vinculado ou do contato) (#232) */
  avatarUrl?: string | null;
}

export interface LeadsDashboard {
  leads: {
    total: number;
    new: number;
    engaged: number;
    converted: number;
    cartAbandoned: number;
    conversionRate: string;
  };
  abandonedCarts: {
    total: number;
    pending: number;
    sent: number;
    recovered: number;
    recoveryRate: string;
    recoveredRevenue: number;
    lostRevenue: number;
  };
}

export interface AbandonedCart {
  id: string;
  tenantId: string;
  remoteJid: string;
  customer?: { id: string; name: string; phone: string } | null;
  order?: { id: string; orderNumber: number; status: string } | null;
  cartData: Array<{ productName: string; quantity: number; unitPrice: number }>;
  cartTotal: number;
  recoveryStatus: 'PENDING' | 'SENT' | 'RECOVERED' | 'EXPIRED' | 'IGNORED';
  createdAt: string;
  recoveredAt: string | null;
}

/**
 * Lista leads com filtros e paginação
 */
export async function listLeads(
  params: PaginationParams & { status?: string; search?: string } = {},
): Promise<{ data: WhatsAppLead[]; meta: ApiMeta }> {
  const queryParams = new URLSearchParams();
  Object.entries(params).forEach(([key, value]) => {
    if (value !== undefined && value !== null && value !== '' && value !== 'all') {
      queryParams.append(key, String(value));
    }
  });

  const response = await api.get<ApiResponse<WhatsAppLead[]>>(
    `/whatsapp/leads?${queryParams.toString()}`,
  );

  if (!response.data.success) {
    throw new Error(response.data.error?.message || 'Erro ao buscar leads');
  }

  return {
    data: response.data.data,
    meta: response.data.meta!,
  };
}

/**
 * Obtém dashboard de leads
 */
export async function getLeadsDashboard(): Promise<LeadsDashboard> {
  const response = await api.get<ApiResponse<LeadsDashboard>>(
    '/whatsapp/leads/dashboard',
  );

  if (!response.data.success) {
    throw new Error(response.data.error?.message || 'Erro ao buscar dashboard de leads');
  }

  return response.data.data;
}

/**
 * Lista carrinhos abandonados
 */
export async function listAbandonedCarts(
  params: PaginationParams & { recoveryStatus?: string } = {},
): Promise<{ data: AbandonedCart[]; meta: ApiMeta }> {
  const queryParams = new URLSearchParams();
  Object.entries(params).forEach(([key, value]) => {
    if (value !== undefined && value !== null && value !== '' && value !== 'all') {
      queryParams.append(key, String(value));
    }
  });

  const response = await api.get<ApiResponse<AbandonedCart[]>>(
    `/whatsapp/abandoned-carts?${queryParams.toString()}`,
  );

  if (!response.data.success) {
    throw new Error(response.data.error?.message || 'Erro ao buscar carrinhos abandonados');
  }

  return {
    data: response.data.data,
    meta: response.data.meta!,
  };
}

export default {
  getStatus,
  connect,
  disconnect,
  logout,
  getQrCode,
  sendMessage,
  listMessages,
  listContacts,
  getChatbotSettings,
  updateChatbotSettings,
  listLeads,
  getLeadsDashboard,
  listAbandonedCarts,
};

// ==========================================================================
// CATÁLOGO DO WHATSAPP BUSINESS (#241)
// ==========================================================================

/** Situação geral do catálogo do WhatsApp (contagens e itens que pedem atenção) */
export async function getCatalogOverview(): Promise<WhatsAppCatalogOverview> {
  const response = await api.get<ApiResponse<WhatsAppCatalogOverview>>('/whatsapp/catalog');
  if (!response.data.success) {
    throw new Error(response.data.error?.message || 'Erro ao buscar a situação do catálogo');
  }
  return response.data.data;
}

/** "Sincronizar catálogo agora" (fica pendente se o WhatsApp estiver desconectado) */
export async function syncCatalog(): Promise<{ queued: boolean; pending: boolean; message?: string }> {
  const response = await api.post<ApiResponse<{ queued: boolean; pending: boolean }> & { message?: string }>(
    '/whatsapp/catalog/sync',
  );
  if (!response.data.success) {
    throw new Error(response.data.error?.message || 'Erro ao sincronizar o catálogo');
  }
  return { ...response.data.data, message: response.data.message };
}

/** Situação dos itens de um produto no catálogo do WhatsApp */
export async function getProductCatalog(productId: string): Promise<WhatsAppProductCatalog> {
  const response = await api.get<ApiResponse<WhatsAppProductCatalog>>(`/whatsapp/catalog/products/${productId}`);
  if (!response.data.success) {
    throw new Error(response.data.error?.message || 'Erro ao buscar a situação do produto no catálogo');
  }
  return response.data.data;
}
